import React, { useState, useEffect } from 'react';
import { PayoffCalculationResults } from '../types';
import { InfoTooltip } from './InfoTooltip';

interface PayoffResultsDisplayProps {
  results: PayoffCalculationResults | null;
  isLoading: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);
};

export const PayoffResultsDisplay: React.FC<PayoffResultsDisplayProps> = ({ results, isLoading }) => {
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (results && !isLoading) {
        const { summary } = results;
        const isInvestingBetter = summary.finalInvestNetWorth > summary.finalPayoffNetWorth;
        const difference = Math.abs(summary.finalInvestNetWorth - summary.finalPayoffNetWorth);
        const higherStrategy = isInvestingBetter ? 'Investing' : 'Paying Off Early';
        const formattedDiff = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(difference);
        
        setAnnouncement(`Calculation complete. ${higherStrategy} results in a higher projected net worth by ${formattedDiff}.`);
    } else {
        setAnnouncement('');
    }
  }, [results, isLoading]);

  if (isLoading) {
    return (
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-bp-dark-blue dark:border-bp-light-blue mx-auto"></div>
        <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Running the numbers...</p>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  const { summary, monthlyData } = results;
  const isInvestingBetter = summary.finalInvestNetWorth > summary.finalPayoffNetWorth;
  const difference = Math.abs(summary.finalInvestNetWorth - summary.finalPayoffNetWorth);
  
  const lastMonth = monthlyData[monthlyData.length-1];
  const finalPayoffHomeEquity = lastMonth.payoffHomeEquity;
  const finalInvestHomeEquity = lastMonth.investHomeEquity;

  return (
    <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
      <div className="sr-only" aria-live="polite" aria-atomic="true">{announcement}</div>
      <h2 className="text-2xl font-bold text-bp-slate dark:text-white mb-4 border-b dark:border-slate-600 pb-2">Results: Pay Off vs. Invest</h2>
      
      <div className={`p-4 rounded-lg mb-6 ${isInvestingBetter ? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600 text-green-900 dark:text-green-100' : 'bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600 text-blue-900 dark:text-blue-100'} border-l-4`}>
        <h3 className="text-xl font-bold">Highest Projected Net Worth</h3>
        <p className="mt-1">
          Based on your inputs, {isInvestingBetter ? 'investing the extra cash' : 'paying off your mortgage early'} is projected to result in a higher net worth by{' '}
          <strong className="text-lg">{formatCurrency(difference)}</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
          <h4 className="font-bold text-lg text-bp-dark-blue dark:text-bp-light-blue mb-2">Strategy 1: Pay Off Early</h4>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li><strong>Final Home Equity:</strong> <span className="font-bold ml-1">{formatCurrency(finalPayoffHomeEquity)}</span></li>
             <li>
                <InfoTooltip text="After your mortgage is paid off, your full monthly payment is invested. This is the final value of that portfolio (pre-tax).">
                    <strong>Final Investment Value:</strong>
                </InfoTooltip>
                 <span className="font-bold ml-1">{formatCurrency(summary.payoffInvestmentDetails.finalValue)}</span>
            </li>
            <li className="pt-2 border-t dark:border-slate-600 mt-2">
                <strong className="text-lg text-blue-800 dark:text-blue-300">Final Net Worth:</strong>
                <span className="font-bold text-lg ml-2 text-blue-800 dark:text-blue-300">{formatCurrency(summary.finalPayoffNetWorth)}</span>
            </li>
            <li className="text-sm text-gray-600 dark:text-gray-400 italic">
                (You'll be debt-free on {summary.newPayoffDate}, {summary.yearsShortened} years early)
            </li>
          </ul>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
          <h4 className="font-bold text-lg text-green-700 dark:text-green-400 mb-2">Strategy 2: Invest the Difference</h4>
           <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li><strong>Final Home Equity:</strong> <span className="font-bold ml-1">{formatCurrency(finalInvestHomeEquity)}</span></li>
            <li>
                <InfoTooltip text="This is the final estimated value of your investments (pre-tax).">
                    <strong>Final Investment Value:</strong>
                </InfoTooltip>
                <span className="font-bold ml-1">{formatCurrency(summary.investInvestmentDetails.finalValue)}</span>
            </li>
            <li className="pt-2 border-t dark:border-slate-600 mt-2">
                <strong className="text-lg text-green-800 dark:text-green-300">Final Net Worth:</strong>
                <span className="font-bold text-lg ml-2 text-green-800 dark:text-green-300">{formatCurrency(summary.finalInvestNetWorth)}</span>
            </li>
             <li className="text-sm text-gray-600 dark:text-gray-400 italic">
                (Your mortgage will be paid off on its original schedule)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};